import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

export default class extends Controller {
  static targets = [
    'status', 'statusDot', 'statusText',
    'alerts', 'activeNow', 'syncHealth'
  ]

  static values = {
    dashboard: String,
    realtimeUrl: String
  }

  connect() {
    this.connected = false
    this.subscription = null
    this.heartbeatInterval = null
    this.reconnectTimeout = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 3
    this.pollingInterval = null
    this.pollingFallbackActive = false

    this.subscribeToChannel()
  }

  disconnect() {
    this.cleanup()
  }

  cleanup() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    this.pollingFallbackActive = false
  }

  // HTML escaping helper for XSS prevention
  escapeHtml(str) {
    if (str == null) return ''
    const string = String(str)
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }
    return string.replace(/[&<>"']/g, (char) => map[char])
  }

  subscribeToChannel() {
    const dashboard = this.dashboardValue || 'executive'

    this.subscription = consumer.subscriptions.create(
      {
        channel: 'Admin::AnalyticsDashboardChannel',
        dashboard: dashboard
      },
      {
        connected: () => this.handleConnected(),
        disconnected: () => this.handleDisconnected(),
        rejected: () => this.handleRejected(),
        received: (data) => this.handleReceived(data)
      }
    )
  }

  handleConnected() {
    this.connected = true
    this.reconnectAttempts = 0
    this.updateStatus('connected')
    this.startHeartbeat()
    this.stopPollingFallback()
  }

  handleDisconnected() {
    this.connected = false
    this.updateStatus('disconnected')
    this.stopHeartbeat()
    this.reconnectAttempts += 1

    // After max reconnect attempts, fall back to HTTP polling
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`WebSocket failed after ${this.reconnectAttempts} attempts, falling back to polling`)
      this.startPollingFallback()
      return
    }

    // Auto-reconnect after 3 seconds
    this.reconnectTimeout = setTimeout(() => {
      if (!this.connected) {
        this.updateStatus('reconnecting')
      }
    }, 3000)
  }

  handleRejected() {
    this.connected = false
    this.updateStatus('rejected')
    this.stopHeartbeat()
    console.error('Analytics channel subscription rejected')
  }

  handleReceived(data) {
    switch (data.event) {
      case 'initial_metrics':
      case 'metrics_update':
        this.updateRealtimeMetrics(data.metrics)
        break
      case 'alert':
        this.showAlert(data.alert)
        break
      case 'pong':
        // Heartbeat acknowledged
        break
      default:
        console.debug('Unknown realtime event:', data.event)
    }
  }

  updateRealtimeMetrics(metrics) {
    if (!metrics) return

    // Update active now
    if (this.hasActiveNowTarget && metrics.active_now !== undefined) {
      this.activeNowTarget.textContent = this.formatNumber(metrics.active_now)
    }

    // Update sync health indicator
    if (this.hasSyncHealthTarget && metrics.sync_health) {
      const healthClasses = {
        healthy: 'bg-green-100 text-green-800',
        degraded: 'bg-yellow-100 text-yellow-800',
        critical: 'bg-red-100 text-red-800'
      }
      const healthClass = healthClasses[metrics.sync_health] || healthClasses.healthy
      this.syncHealthTarget.className =
        `px-3 py-1.5 text-xs font-medium uppercase tracking-wide border-2 border-neutral-900 ${healthClass}`
      this.syncHealthTarget.textContent = metrics.sync_health.toUpperCase()
    }
  }

  showAlert(alert) {
    if (!this.hasAlertsTarget || !alert) return

    const severityClasses = {
      critical: 'bg-red-100 border-red-500 text-red-800',
      warning: 'bg-yellow-100 border-yellow-500 text-yellow-800',
      info: 'bg-blue-100 border-blue-500 text-blue-800'
    }

    const severityClass = severityClasses[alert.severity] || severityClasses.info
    // eslint-disable-next-line max-len
    const alertIconPath = 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
    const alertHtml = `
      <div class="flex items-center gap-3 p-4 border-2 ${severityClass}">
        <svg class="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${alertIconPath}"></path>
        </svg>
        <div>
          <span class="text-xs font-semibold uppercase">${this.escapeHtml(alert.type)}</span>
          <p class="text-sm">${this.escapeHtml(alert.message)}</p>
        </div>
        <button type="button" onclick="this.parentElement.remove()" class="ml-auto">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `

    this.alertsTarget.insertAdjacentHTML('afterbegin', alertHtml)

    // Auto-dismiss after 30 seconds
    setTimeout(() => {
      const firstAlert = this.alertsTarget.querySelector(':first-child')
      if (firstAlert) firstAlert.remove()
    }, 30000)
  }

  updateStatus(status) {
    if (!this.hasStatusTarget) return

    const statusConfig = {
      connected: {
        dotClass: 'bg-green-500 animate-pulse',
        text: 'LIVE',
        statusClass: 'bg-green-100'
      },
      disconnected: {
        dotClass: 'bg-red-500',
        text: 'OFFLINE',
        statusClass: 'bg-red-100'
      },
      reconnecting: {
        dotClass: 'bg-yellow-500 animate-pulse',
        text: 'RECONNECTING...',
        statusClass: 'bg-yellow-100'
      },
      rejected: {
        dotClass: 'bg-red-500',
        text: 'ACCESS_DENIED',
        statusClass: 'bg-red-100'
      },
      polling: {
        dotClass: 'bg-blue-500 animate-pulse',
        text: 'POLLING',
        statusClass: 'bg-blue-100'
      }
    }

    const config = statusConfig[status] || statusConfig.disconnected

    if (this.hasStatusDotTarget) {
      this.statusDotTarget.className = `w-2 h-2 rounded-full ${config.dotClass}`
    }
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = config.text
    }
  }

  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatInterval = setInterval(() => {
      if (this.subscription && this.connected) {
        this.subscription.perform('heartbeat')
      }
    }, 30000)
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  refresh() {
    if (this.subscription && this.connected) {
      this.subscription.perform('refresh')
    } else if (this.hasRealtimeUrlValue) {
      // Fallback to HTTP polling if WebSocket not connected
      this.fetchRealtimeData()
    }
  }

  async fetchRealtimeData() {
    try {
      const response = await fetch(this.realtimeUrlValue)
      const data = await response.json()
      if (data.metrics) {
        this.updateRealtimeMetrics(data.metrics)
      }
    } catch (error) {
      console.error('Failed to fetch realtime data:', error)
    }
  }

  formatNumber(num) {
    return new Intl.NumberFormat().format(num)
  }

  startPollingFallback() {
    if (this.pollingFallbackActive || !this.hasRealtimeUrlValue) return

    this.pollingFallbackActive = true
    this.updateStatus('polling')

    // Poll every 30 seconds as fallback
    this.pollingInterval = setInterval(() => {
      this.fetchRealtimeData()
    }, 30000)

    // Fetch immediately
    this.fetchRealtimeData()
  }

  stopPollingFallback() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
    this.pollingFallbackActive = false
  }
}
